import React from 'react';
import { motion } from 'framer-motion';

/*
  Canon Gauge
  - Presents a horizontal track with animated progress and a centered needle
  - Theme-aware using succession CSS variables
  - Minimal API for reuse across products

  Props:
    currentValue: number
    lowValue: number
    highValue: number
    format?: (n:number) => string  // formats the badge text
    showBadge?: boolean             // show the floating value badge
    needleOffsetPx?: number         // visual tweak to align needle exactly to track (default -6)
    className?: string
*/
const Gauge = ({
  currentValue = 0,
  lowValue = 0,
  highValue = 0,
  format = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(n || 0)),
  showBadge = true,
  needleOffsetPx = -6,
  className = '',
}) => {
  const range = Number(highValue) - Number(lowValue);
  const pct = range > 0 ? ((Number(currentValue) - Number(lowValue)) / range) * 100 : 0;
  const percentage = Math.max(0, Math.min(100, pct));

  return (
    <div className={`relative w-full h-32 ${className}`}>
      {/* Background track */}
      <div
        className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-3 rounded-full"
        style={{
          background: 'linear-gradient(to right, var(--succession-error-20, rgba(239,68,68,.2)), var(--succession-warning-20, rgba(234,179,8,.2)), var(--succession-success-20, rgba(16,185,129,.2)))',
        }}
      />

      {/* Progress fill */}
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${percentage}%` }}
        transition={{ duration: 1.5, ease: 'easeOut', delay: 0.3 }}
        className="absolute left-0 top-1/2 -translate-y-1/2 h-3 rounded-full shadow-lg"
        style={{ background: 'linear-gradient(to right, hsl(160,84%,39%), hsl(142,76%,36%))', boxShadow: '0 0 12px rgba(16,185,129,.5)' }}
      />

      {/* Needle */}
      <motion.div
        initial={{ left: '0%', opacity: 0 }}
        animate={{ left: `${percentage}%`, opacity: 1 }}
        transition={{ duration: 1.5, ease: 'easeOut', delay: 0.3 }}
        className="absolute -translate-x-1/2 h-20"
        style={{ left: `${percentage}%`, top: `calc(20%)` }}
      >
        <div className="relative h-full">
          <div
            className="absolute top-0 left-1/2 -translate-x-1/2 w-1 h-full rounded-full shadow-lg"
            style={{ background: 'linear-gradient(to bottom, hsl(142,76%,36%), hsl(142,71%,45%))' }}
          />
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-5 h-5 bg-green-500 rounded-full border-4 border-white shadow-xl">
            <div className="absolute inset-0 bg-green-400 rounded-full animate-ping opacity-75" />
            <div className="absolute inset-0 bg-green-500 rounded-full animate-pulse" />
          </div>
          {showBadge && (
            <motion.div initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5, delay: 1.8 }} className="absolute -top-16 left-1/2 -translate-x-1/2 whitespace-nowrap">
              <div className="bg-green-500 text-white px-4 py-2 rounded-lg font-semibold shadow-lg">{format(currentValue)}</div>
              <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-green-500 rotate-45" />
            </motion.div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default Gauge;


